import {isUndefined} from 'lodash/fp';

import {FilterDialogSectionId} from 'filters/features/FilterDialog/types';
import {assertPolicyFilter, isPolicyFilter} from 'filters/types';
import {FilterTopbarItemId} from 'filters/features/FilterTopbar/types';

import {createTreeViewSection} from 'filters/features/FilterDialog/sections/TreeViewSection/TreeViewSection';
import {useMarketTree} from 'modules/decisionsNext/hooks/trees/useMarketTree';
import {useMarketTreeViewSelection} from 'features/GlobalFilterProvider/filters/markets/useMarketTreeViewSelection';
import {createValueItem} from 'filters/features/FilterTopbar/items/ValueItem/ValueItem';
import {openFilterDialog} from 'filters';
import {createCounterItem} from 'filters/features/FilterTopbar/items/CounterItem/CounterItem';

import MarketIcon from 'components/icons/metrics/carriers.svg';

export const marketDialogSection = createTreeViewSection({
  id: FilterDialogSectionId.Markets,
  title: 'Markets',
  icon: MarketIcon,
  useTreeData: useMarketTree,
  getDataOptions: value => ({
    skip:
      Object.values(value.carrierIds).every(ids => !ids?.length) &&
      Object.values(value.intermediaryIds).every(ids => !ids?.length),
  }),
  useTreeSelection: useMarketTreeViewSelection,
  getValue: filter => {
    assertPolicyFilter(filter);

    return {
      carrierIds: {
        childIds: filter.issuingPaperIds ?? undefined,
        parentIds: filter.parentCarrierIds ?? undefined,
        ultimateParentIds: filter.ultimateCarrierIds ?? undefined,
      },
      intermediaryIds: {
        childIds: filter.childIntermediaryIds ?? undefined,
        parentIds: filter.parentIntermediaryIds ?? undefined,
        ultimateParentIds: filter.ultimateIntermediaryIds ?? undefined,
      },
      direct: filter.direct ?? undefined,
      brokerMarketStatuses: filter.brokerMarketStatuses ?? null,
    };
  },
  onChange: (filter, value) => {
    assertPolicyFilter(filter);

    return {
      ...filter,
      direct: value.direct,
      brokerMarketStatuses: value.brokerMarketStatuses,
      issuingPaperIds: value.carrierIds.childIds,
      parentCarrierIds: value.carrierIds.parentIds,
      ultimateCarrierIds: value.carrierIds.ultimateParentIds,
      childIntermediaryIds: value.intermediaryIds.childIds,
      parentIntermediaryIds: value.intermediaryIds.parentIds,
      ultimateIntermediaryIds: value.intermediaryIds.ultimateParentIds,
    };
  },
});

export const marketTopbarItems = [
  createValueItem({
    id: FilterTopbarItemId.MarketPlacement,
    getValue: filter => {
      if (!isPolicyFilter(filter)) {
        return null;
      }

      if (isUndefined(filter.direct)) {
        return null;
      }

      return filter.direct ? 'Direct' : 'Indirect';
    },
    onClick: () => openFilterDialog(FilterDialogSectionId.Markets),
    onReset: filter => {
      assertPolicyFilter(filter);

      return {
        ...filter,
        direct: undefined,
      };
    },
  }),
  createCounterItem({
    id: FilterTopbarItemId.CarrierType,
    entity: 'Carrier Type',
    getCount: filter => {
      if (!isPolicyFilter(filter)) {
        return 0;
      }

      return filter.brokerMarketStatuses?.length;
    },
    onClick: () => openFilterDialog(FilterDialogSectionId.Markets),
    onReset: filter => {
      assertPolicyFilter(filter);

      return {
        ...filter,
        brokerMarketStatuses: undefined,
      };
    },
  }),
  createCounterItem({
    id: FilterTopbarItemId.Carrier,
    entity: 'Carriers',
    getCount: filter => {
      if (!isPolicyFilter(filter)) {
        return 0;
      }

      return [filter.ultimateCarrierIds, filter.parentCarrierIds, filter.issuingPaperIds].reduce(
        (acc, ids) => acc + (ids?.length || 0),
        0
      );
    },
    onClick: () => openFilterDialog(FilterDialogSectionId.Markets),
    onReset: filter => {
      assertPolicyFilter(filter);

      return {
        ...filter,
        ultimateCarrierIds: undefined,
        parentCarrierIds: undefined,
        issuingPaperIds: undefined,
      };
    },
  }),
  createCounterItem({
    id: FilterTopbarItemId.Intermediary,
    entity: 'Intermediaries',
    getCount: filter => {
      if (!isPolicyFilter(filter)) {
        return 0;
      }

      return [filter.ultimateIntermediaryIds, filter.parentIntermediaryIds, filter.childIntermediaryIds].reduce(
        (acc, ids) => acc + (ids?.length || 0),
        0
      );
    },
    onClick: () => openFilterDialog(FilterDialogSectionId.Markets),
    onReset: filter => {
      assertPolicyFilter(filter);

      return {
        ...filter,
        ultimateIntermediaryIds: undefined,
        parentIntermediaryIds: undefined,
        childIntermediaryIds: undefined,
      };
    },
  }),
];
